import { z } from 'zod'
import { Address, ChainId, Cursor, Hash32, IsoTimestamp, MicroString, PageLimit, UsdString, Uuid, WeiString } from '../primitives.js'
import {
  AgentSubscriptionStatus,
  EnvelopeKind,
  EnvelopeOwnerKind,
  EnvelopeStatus,
  PaymentIntentStatus,
  PaymentKind,
  PlanId,
  PlanSubscriptionStatus,
} from '../enums.js'
import { AgentSummary } from './common.js'
import { BrainId } from './agents.js'
import { UnsignedTx } from './tokens.js'

export const PlanFeatures = z.object({
  /** Live trading: authorizing agents on arbitrage trading wallets. */
  liveTrading: z.boolean(),
  apiKeys: z.boolean(),
  externalAgents: z.boolean(),
  prioritySupport: z.boolean(),
  /** Same as the plan's top-level limits, repeated so `features` is self-contained. */
  maxAgents: z.number().int().nonnegative(),
  maxPermissions: z.number().int().nonnegative(),
  bullets: z.array(z.string()),
})
export type PlanFeatures = z.output<typeof PlanFeatures>

export const Plan = z.object({
  id: PlanId,
  name: z.string(),
  priceUsdMonth: UsdString,
  includedCreditsMicro: MicroString,
  maxAgents: z.number().int().nonnegative(),
  /** Active and paused customer permissions across the user's wallets. */
  maxPermissions: z.number().int().nonnegative(),
  features: PlanFeatures,
  sort: z.number().int(),
  enabled: z.boolean(),
})
export type Plan = z.output<typeof Plan>

export const RateCard = z.object({
  id: Uuid,
  brainId: BrainId,
  inputMicroPer1k: MicroString,
  outputMicroPer1k: MicroString,
  markupBps: z.number().int().min(0),
  effectiveFrom: IsoTimestamp,
  effectiveTo: IsoTimestamp.nullable(),
})
export type RateCard = z.output<typeof RateCard>

export const CreditEnvelope = z.object({
  id: Uuid,
  ownerKind: EnvelopeOwnerKind,
  ownerId: Uuid.nullable(),
  kind: EnvelopeKind,
  limitMicro: MicroString,
  settledMicro: MicroString,
  heldMicro: MicroString,
  /** limit − settled − held */
  availableMicro: MicroString,
  periodStart: IsoTimestamp,
  periodEnd: IsoTimestamp,
  status: EnvelopeStatus,
})
export type CreditEnvelope = z.output<typeof CreditEnvelope>

export const CreditsSummary = z.object({
  availableMicro: MicroString,
  heldMicro: MicroString,
  settledMicro: MicroString,
  envelopes: z.array(CreditEnvelope),
})
export type CreditsSummary = z.output<typeof CreditsSummary>

export const Topup = z.object({ amountUsd: UsdString })
export type Topup = z.input<typeof Topup>

/**
 * A payment the user's wallet makes to `PaymentTreasury` (USDG on Robinhood Chain). `ref` binds it to
 * (user, kind, amount, nonce) and is single-use on chain. `provider: 'stub'` intents confirm instantly
 * (produced only by test doubles, ADR-0008); `usdg` intents confirm when the `payments.index`
 * job sees `PaymentReceived`. Pending intents expire after one hour; a payment that still arrives later is
 * honoured.
 */
export const PaymentIntent = z.object({
  id: Uuid,
  provider: z.enum(['usdg', 'stub']),
  kind: PaymentKind,
  status: PaymentIntentStatus,
  amountUsd: UsdString,
  chainId: ChainId,
  treasury: Address,
  token: Address,
  tokenSymbol: z.string(),
  tokenDecimals: z.number().int().min(0).max(36),
  /** `amountUsd` in token units. */
  amountUnits: WeiString,
  ref: Hash32,
  planId: PlanId.nullable(),
  agent: AgentSummary.nullable(),
  tier: z.string().nullable(),
  payer: Address.nullable(),
  txHash: Hash32.nullable(),
  createdAt: IsoTimestamp,
  expiresAt: IsoTimestamp,
  confirmedAt: IsoTimestamp.nullable(),
})
export type PaymentIntent = z.output<typeof PaymentIntent>

export const PaymentsQuery = z.object({ cursor: Cursor.optional(), limit: PageLimit })
export const ChoosePlan = z.object({ planId: PlanId })
export type ChoosePlan = z.input<typeof ChoosePlan>

export const PlanSubscription = z.object({
  id: Uuid,
  planId: PlanId,
  status: PlanSubscriptionStatus,
  periodStart: IsoTimestamp,
  periodEnd: IsoTimestamp,
})
export type PlanSubscription = z.output<typeof PlanSubscription>

export const SubscribeAgent = z.object({ tier: z.string().min(1).max(40) })
export type SubscribeAgent = z.input<typeof SubscribeAgent>

export const AgentSubscription = z.object({
  id: Uuid,
  agent: AgentSummary,
  tier: z.string(),
  priceUsdMonth: UsdString,
  status: AgentSubscriptionStatus,
  startedAt: IsoTimestamp,
  endsAt: IsoTimestamp.nullable(),
  /** The subscriber's permissions naming this agent (`accounts.permissions.permission_ref`), newest first. */
  permissionRefs: z.array(z.string()),
})
export type AgentSubscription = z.output<typeof AgentSubscription>

/**
 * Result of `POST /me/credits/topup`, `POST /me/plan` and `POST /agents/:handle/subscribe`: the intent and
 * the transactions for the user's wallet (`approve` when the allowance is short, then `pay`). A confirmed
 * intent has no transactions and carries what it activated.
 */
export const PaymentIntentResult = z.object({
  intent: PaymentIntent,
  txs: z.array(UnsignedTx),
  envelope: CreditEnvelope.optional(),
  plan: PlanSubscription.optional(),
  subscription: AgentSubscription.optional(),
})
export type PaymentIntentResult = z.output<typeof PaymentIntentResult>

export const MySubscriptions = z.object({
  plan: PlanSubscription.nullable(),
  agents: z.array(AgentSubscription),
})
export type MySubscriptions = z.output<typeof MySubscriptions>
